import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const interestTypes = [
  { value: "technical-overview", label: "Request Technical Overview" },
  { value: "architecture-briefing", label: "Architecture Briefing" },
  { value: "integration-discussion", label: "Integration / Pilot Discussion" },
  { value: "investor-inquiry", label: "Investor Inquiry" },
  { value: "partnership", label: "Partnership Inquiry" },
  { value: "engineering-interest", label: "Engineering Interest / Careers" },
  { value: "other", label: "Other" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", interest_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.interest_type || !form.message) {
      setError("All fields are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", company: "", email: "", interest_type: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="text-center max-w-md px-6">
          <CheckCircle size={48} weight="regular" className="text-[#4b7399] mx-auto mb-6" />
          <h2 data-testid="contact-success-message" className="text-2xl font-bold text-[#f2f4f8] mb-4">Inquiry Received</h2>
          <p className="text-[#8c9eb3] mb-8">
            Thank you. Your inquiry has been recorded and our team will respond within two business days.
          </p>
          <button
            onClick={() => setSuccess(false)}
            data-testid="contact-submit-another"
            className="font-mono text-xs tracking-wider uppercase text-[#4b7399] hover:text-[#f2f4f8] transition-colors"
          >
            Submit another inquiry
          </button>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="pt-16">
      <section data-testid="contact-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Info */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Contact</p>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
                Get in Touch
              </h1>
              <p className="text-base text-[#8c9eb3] leading-relaxed mb-10 max-w-md">
                For habitat integrators, rover developers, lunar infrastructure teams, and technical partners. We respond to all substantive inquiries.
              </p>

              <div className="space-y-6">
                <div className="border-l-2 border-[#4b7399] pl-6 py-2">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">Technical Overview</p>
                  <p className="text-sm text-[#8c9eb3]">System architecture and capability summary available on request.</p>
                </div>
                <div className="border-l-2 border-[#2d3239] pl-6 py-2">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">Architecture Briefing</p>
                  <p className="text-sm text-[#8c9eb3]">Detailed subsystem integration discussion for primes and mission teams.</p>
                </div>
                <div className="border-l-2 border-[#2d3239] pl-6 py-2">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">Investor Relations</p>
                  <p className="text-sm text-[#8c9eb3]">Deck and technical concept available for qualified investors.</p>
                </div>
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Name</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] focus-visible:border-[#4b7399] placeholder:text-[#8c9eb3]/50"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Organization</Label>
                  <Input
                    id="company"
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Company or organization"
                    className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] focus-visible:border-[#4b7399] placeholder:text-[#8c9eb3]/50"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="work@example.com"
                    className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] focus-visible:border-[#4b7399] placeholder:text-[#8c9eb3]/50"
                  />
                </div>
                <div>
                  <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Interest Type</Label>
                  <Select value={form.interest_type} onValueChange={(val) => setForm({ ...form, interest_type: val })}>
                    <SelectTrigger data-testid="contact-interest-select" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus:ring-1 focus:ring-[#4b7399]">
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121519] border-[#2d3239] rounded-none">
                      {interestTypes.map((type) => (
                        <SelectItem
                          key={type.value}
                          value={type.value}
                          className="text-[#f2f4f8] focus:bg-[#1a1e24] focus:text-[#f2f4f8] rounded-none"
                        >
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Describe your interest or requirement"
                    rows={5}
                    className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] focus-visible:border-[#4b7399] placeholder:text-[#8c9eb3]/50 resize-none"
                  />
                </div>

                {error && (
                  <p data-testid="contact-error-message" className="text-sm text-[#d97706] font-mono">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="w-full bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none border border-transparent hover:bg-[#3a5a78] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <><Spinner size={16} className="animate-spin" /> Submitting...</> : "Submit Inquiry"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
